'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/calc-extra'

const CurvatureExplorer = dynamic(() => import('../_components/widgets/CalcExtraWidgets').then(m => m.CurvatureExplorer), { ssr: false })
const LossSurface = dynamic(() => import('../_components/widgets/three/LossSurface'), { ssr: false })

export default function MinimaAndSaddles() {
  return (
    <Lesson>
      <Step>
        <H>Flat isn’t always the bottom</H>
        <P>
          Gradient descent stops when the slope is zero. You might hope that means “lowest point”, but a flat spot can be three
          different things: the bottom of a valley (a <strong>minimum</strong>), the top of a hill (a <strong>maximum</strong>),
          or a ledge where the ground levels off and then keeps going down.
        </P>
        <P>
          To tell them apart, look at how the slope is <em>changing</em>: the <strong>second derivative</strong>, written{' '}
          <M>{"f''(x)"}</M>. It measures the bend. Positive means the curve bends upwards like a cup; negative means it bends
          down like a cap.
        </P>
        <Figure n="4.10" wide caption="Drag the point. Blue is the slope; the dashed parabola shows the bend. At a flat spot, the bend tells you which kind it is.">
          <CurvatureExplorer />
        </Figure>
      </Step>

      <Step gate={['q-bend', 'classify']}>
        <H>The second derivative test</H>
        <MB>{"f'(x) = 0 \\text{ and } f''(x) > 0 \\Rightarrow \\text{minimum} \\qquad f'(x) = 0 \\text{ and } f''(x) < 0 \\Rightarrow \\text{maximum}"}</MB>
        <Quiz
          id="q-bend"
          question={
            <>
              For <M>{'f(x) = x^3'}</M>, the slope at 0 is 0 and the bend at 0 is also 0. What kind of point is it?
            </>
          }
          options={[
            { label: 'Neither — the curve flattens out, then keeps climbing', correct: true },
            { label: 'A minimum', why: 'Try x = −0.5: f is negative there, so 0 isn’t the lowest point around.' },
            { label: 'A maximum', why: 'Try x = 0.5: f is bigger there.' },
          ]}
          explain="When the bend is also 0, the test can’t decide. Pick the ‘x³’ curve above and drag through 0 to see the pause."
        />
        <Exercise
          id="classify"
          title="secondDerivative and classify"
          {...EX.classify}
          hints={[
            <>
              <C>{'(f(x + h) - 2 * f(x) + f(x - h)) / (h * h)'}</C> with <C>const h = 1e-4</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="restarts">
        <H>Which valley do you find?</H>
        <P>
          A function can have many valleys. The deepest is the <strong>global minimum</strong>; the others are{' '}
          <strong>local minima</strong>. Gradient descent only ever walks downhill, so it ends up in whichever valley it starts
          above. One simple fix: start from several places, and keep the best result.
        </P>
        <Exercise
          id="restarts"
          title="descend and bestOfStarts"
          {...EX.restarts}
          hints={[
            <>
              <C>{'let x = x0'}</C>, then 200 times: <C>{'x -= 0.05 * slope(x)'}</C>.
            </>,
            <>Keep the end point with the smallest f(x) seen so far.</>,
          ]}
        />
      </Step>

      <Step>
        <H>Saddles: the 3-D trap</H>
        <P>
          With two or more inputs there’s a new kind of flat spot: the <strong>saddle</strong>. It curves up one way and down the
          other, like a horse’s saddle or a mountain pass. Pick “saddle” below and press <em>Roll downhill</em>: the ball rolls
          straight along the ridge and stops dead in the middle, where every slope is 0. Then try <em>Random start</em>.
        </P>
        <Figure n="4.11" wide caption="Saddle: flat in the middle, but not a minimum. Two valleys: gradient descent settles in the shallow one if it starts there. Drag to orbit.">
          <LossSurface mode="descent" surfaces={['saddle', 'wells', 'bumpy']} />
        </Figure>
        <Callout kind="why">
          Neural networks have millions of inputs (their weights). In that many dimensions, true local minima turn out to be
          rare. Saddle points are everywhere. Being nudged off a saddle by the tiny randomness in training is one reason
          real networks train at all.
        </Callout>
      </Step>

      <Step gate={['q-saddle', 'hessian']}>
        <H>Bend in two directions</H>
        <P>
          With two inputs, the bend becomes a 2 × 2 grid of second derivatives called the <strong>Hessian</strong>. Its
          eigenvalues (Module 3!) are the bends along its eigenvector directions: both positive means a valley, both negative a
          hill, one of each a saddle. A quick test for that is the <strong>determinant</strong>:
        </P>
        <MB>{'d = f_{xx}\\,f_{yy} - f_{xy}^2 \\qquad d < 0 \\Rightarrow \\text{saddle}'}</MB>
        <Quiz
          id="q-saddle"
          question={
            <>
              At the flat point (0, 0) of <M>{'f(x, y) = x^2 - y^2'}</M>, which way should gradient descent go to get lower?
            </>
          }
          options={[
            { label: 'Along y (either way) — that direction curves down', correct: true },
            { label: 'Along x', why: 'x² curves up, so moving in x makes f bigger.' },
            { label: 'Nowhere — it’s already the lowest point', why: 'f(0, 1) = −1 is lower than f(0, 0) = 0.' },
          ]}
        />
        <Exercise
          id="hessian"
          title="hessian and kind"
          {...EX.hessian}
          hints={[
            <>Copy the mixed-derivative formula from the comment into fxy.</>,
            <>
              <C>{'const [[fxx, fxy], [, fyy]] = hessian(f, x, y)'}</C>, then compare <C>d</C> and <C>fxx</C> with 0.
            </>,
          ]}
        />
        <Callout kind="note">
          Shapes with only one valley and no hills, like a bowl, are called <strong>convex</strong>. On a convex loss, gradient
          descent always finds the global minimum. Linear regression (Module 6) has a convex loss. Neural networks don’t —
          which is why training them is part science, part craft.
        </Callout>
      </Step>
    </Lesson>
  )
}
